import { useDragAndDropContext } from '@ardoq/drag-and-drop';
import {
  groupBoxStyle,
  HeaderRowWrapper,
  ViewOptionsClickableIcon,
} from '../../atoms';
import { IconName, Icon } from '@ardoq/icons';
import { DragButton } from '@ardoq/button';
import { Select } from '@ardoq/select';
import { Checkbox, TextInput } from '@ardoq/forms';
import { FlexBox, Stack } from '@ardoq/layout';
import { perspectivesRowDragAndDropHandlers } from '@ardoq/perspectives';
import { colors, spacing } from '@ardoq/design-tokens';
import { header4 } from '@ardoq/typography';
import styled from 'styled-components';
import type { LabelFormattingComponentProperties } from '../../types';
import { WarningNotification } from '@ardoq/status-ui';
import { FieldsSelect } from './FieldsSelect';
import {
  getSelectedLabelFieldOptions,
  getLabelFormattingTypeLabel,
} from './utils';
import { disabledCSS } from '@ardoq/style-helpers';

const Label = styled.label`
  ${header4}
`;

const LabelFormattingBox = styled.div`
  ${groupBoxStyle}
  gap: ${spacing.s8};
  display: flex;
  flex-direction: column;
  align-items: start;
  user-select: none;
`;

const PickersContainer = styled(Stack).attrs({ gap: 'medium' })<{
  $isDisabled: boolean;
}>`
  width: 100%;
  ${props => props.$isDisabled && disabledCSS}
`;

const DisableHeaderWrapper = styled.div<{
  $isDisabled: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${spacing.s8};
  ${props => props.$isDisabled && disabledCSS}
`;

const RightAlignedViewOptionsClickableIcon = styled(ViewOptionsClickableIcon)`
  margin-left: auto;
`;

const LabelFormattingComponent = ({
  labelFormatting,
  onLabelFormattingDeleted,
  onUpdateLabelFormattingAt,
  isComponentFormatting,
  isSelectedTypeValid,
  typeOptions,
  fieldOptions,
  isDisabled,
  index,
  disabledCheckboxMessage,
}: LabelFormattingComponentProperties) => {
  const { onDragStart, onDragOver, onDragEnd } =
    perspectivesRowDragAndDropHandlers(
      labelFormatting.__id,
      useDragAndDropContext()
    );
  const selectedOptions = getSelectedLabelFieldOptions(
    labelFormatting,
    fieldOptions
  );
  const hasFieldsWarning =
    selectedOptions.length > 4 && labelFormatting.fields.length > 4;

  const selectTypePlaceholder = `Search and select ${
    isComponentFormatting ? 'component' : 'reference'
  } type`;

  return (
    <LabelFormattingBox
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <HeaderRowWrapper>
        <DragButton draggable />
        <DisableHeaderWrapper $isDisabled={isDisabled}>
          {labelFormatting.type === 'component' ? (
            <>
              <Icon color={colors.icon} iconName={IconName.COMPONENT} />{' '}
              Component label
            </>
          ) : (
            <>
              <Icon color={colors.icon} iconName={IconName.REFERENCE} />{' '}
              Reference label
            </>
          )}
        </DisableHeaderWrapper>
        <RightAlignedViewOptionsClickableIcon
          iconName={IconName.DELETE}
          label="Delete label formatting rule"
          onClick={() => onLabelFormattingDeleted(index)}
          data-testid="delete-multilabel-rule-button"
        />
      </HeaderRowWrapper>
      <PickersContainer $isDisabled={isDisabled}>
        <Stack gap="small">
          {!isSelectedTypeValid && (
            <WarningNotification>
              This label can&apos;t be applied to the opened datasets.
            </WarningNotification>
          )}
          <Label>
            {labelFormatting.type === 'component'
              ? 'Component type'
              : 'Reference type '}
          </Label>
          {isDisabled ? (
            <TextInput
              value={
                getLabelFormattingTypeLabel(labelFormatting, typeOptions) ||
                selectTypePlaceholder
              }
            />
          ) : (
            <Select
              menuPortalTarget={null}
              placeholder={selectTypePlaceholder}
              value={labelFormatting.typeName}
              options={typeOptions}
              isClearable={false}
              onChange={option =>
                onUpdateLabelFormattingAt(index, { typeName: option?.value })
              }
              allowNullOptionValue
            />
          )}
        </Stack>
        <Stack gap="small">
          <Label>Field</Label>
          <FieldsSelect
            index={index}
            isDisabled={isDisabled}
            fieldOptions={fieldOptions}
            selectedOptions={selectedOptions}
            hasFieldsWarning={hasFieldsWarning}
            onUpdateLabelFormattingAt={onUpdateLabelFormattingAt}
          />
        </Stack>
        {isDisabled ? null : (
          <FlexBox align={'center'}>
            <Checkbox
              isChecked={labelFormatting.showFieldName}
              popoverContent={disabledCheckboxMessage}
              isDisabled={Boolean(disabledCheckboxMessage)}
              onChange={showFieldName =>
                onUpdateLabelFormattingAt(index, { showFieldName })
              }
            >
              Show field name on label
            </Checkbox>
          </FlexBox>
        )}
      </PickersContainer>
    </LabelFormattingBox>
  );
};
export { LabelFormattingComponent };
